import React, { useState, useRef, useEffect, useMemo, ReactNode, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SelectProps {
  children: ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}

interface SelectTriggerProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  isOpen?: boolean;
}

interface SelectValueProps {
  placeholder?: string;
  children?: ReactNode;
}

interface SelectContentProps {
  children: ReactNode;
  className?: string;
}

interface SelectItemProps {
  children: ReactNode;
  value: string;
  className?: string;
  disabled?: boolean;
}

export function Select({ children, value, onValueChange, disabled }: SelectProps) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !(target as Element).closest('[data-select-content]')
      ) {
        if (open) handleClose();
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleValueChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    }
    // Don't close immediately - let the parent update first
    setTimeout(() => {
      handleClose();
    }, 100);
  };

  // Extract options from SelectContent children
  useEffect(() => {
    const extractOptions = () => {
      const items: Array<{ value: string; label: string }> = [];
      React.Children.forEach(children, child => {
        if (!React.isValidElement(child)) return;
        const childType = child.type as any;
        if (childType?.displayName === 'SelectContent') {
          const contentProps = child.props as { children?: ReactNode };
          React.Children.forEach(contentProps.children, (item: ReactNode) => {
            if (React.isValidElement(item) && (item.type as any)?.displayName === 'SelectItem') {
              const itemProps = item.props as {
                value: string;
                children?: ReactNode;
                disabled?: boolean;
              };
              // Skip disabled items
              if (!itemProps.disabled) {
                // Get label from children - handle both string and ReactNode
                let label = String(itemProps.value);

                // Handle string children directly
                if (typeof itemProps.children === 'string') {
                  label = itemProps.children;
                }
                // Handle number children
                else if (typeof itemProps.children === 'number') {
                  label = String(itemProps.children);
                }
                // Handle React element children
                else if (React.isValidElement(itemProps.children)) {
                  // Try to extract text content from React element
                  // For simple cases like {dept.name}, the children might be directly accessible
                  const children = (itemProps.children as any)?.props?.children;
                  if (typeof children === 'string') {
                    label = children;
                  } else if (typeof children === 'number') {
                    label = String(children);
                  } else if (React.isValidElement(children)) {
                    // Nested React element - try to get text content recursively
                    const nestedChildren = (children as any)?.props?.children;
                    if (typeof nestedChildren === 'string') {
                      label = nestedChildren;
                    } else if (typeof nestedChildren === 'number') {
                      label = String(nestedChildren);
                    }
                  }
                }

                items.push({
                  value: String(itemProps.value),
                  label: label,
                });
              }
            }
          });
        }
      });
      setOptions(items);
    };

    extractOptions();
  }, [children]);

  return (
    <div className="relative" ref={containerRef}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return null;
        const childType = child.type as any;

        if (childType?.displayName === 'SelectTrigger') {
          return React.cloneElement(child as ReactElement<any>, {
            onClick: () => {
              if (!disabled) {
                if (open) {
                  handleClose();
                } else {
                  setOpen(true);
                }
              }
            },
            disabled,
            isOpen: open,
          });
        }

        if (childType?.displayName === 'SelectValue') {
          // Normalize value to string - handle empty string, null, undefined
          const stringValue =
            value !== undefined && value !== null && value !== '' ? String(value) : '';

          // Find label from options first (most reliable)
          let foundLabel: string | undefined = undefined;
          if (stringValue && options && options.length > 0) {
            const found = options.find(opt => {
              const optVal = String(opt.value).trim().toLowerCase();
              const val = String(stringValue).trim().toLowerCase();
              return optVal === val;
            });
            foundLabel = found?.label;
          }

          // If not found in options, search directly from SelectContent children as fallback
          if (!foundLabel && stringValue) {
            React.Children.forEach(children, contentChild => {
              if (!React.isValidElement(contentChild)) return;
              const contentChildType = contentChild.type as any;
              if (contentChildType?.displayName === 'SelectContent') {
                const contentProps = contentChild.props as { children?: ReactNode };
                React.Children.forEach(contentProps.children, (item: ReactNode) => {
                  if (
                    React.isValidElement(item) &&
                    (item.type as any)?.displayName === 'SelectItem'
                  ) {
                    const itemProps = item.props as {
                      value: string;
                      children?: ReactNode;
                      disabled?: boolean;
                    };
                    if (!itemProps.disabled) {
                      const itemValue = String(itemProps.value).trim().toLowerCase();
                      const currentValue = String(stringValue).trim().toLowerCase();
                      if (itemValue === currentValue) {
                        // Get label from children
                        if (typeof itemProps.children === 'string') {
                          foundLabel = itemProps.children;
                        } else if (typeof itemProps.children === 'number') {
                          foundLabel = String(itemProps.children);
                        } else if (React.isValidElement(itemProps.children)) {
                          const textContent = (itemProps.children as any)?.props?.children;
                          foundLabel = typeof textContent === 'string' ? textContent : undefined;
                        }
                      }
                    }
                  }
                });
              }
            });
          }

          return React.cloneElement(child as ReactElement<any>, {
            key: `select-value-${stringValue}-${foundLabel}`,
            value: stringValue,
            options,
            foundLabel,
          });
        }

        if (childType?.displayName === 'SelectContent') {
          return open
            ? React.cloneElement(child as ReactElement<any>, {
                closing,
                onValueChange: handleValueChange,
                currentValue: value,
                containerRef,
              })
            : null;
        }

        return child;
      })}
    </div>
  );
}

export function SelectTrigger({
  children,
  className = '',
  onClick,
  disabled,
  isOpen,
}: SelectTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
      <ChevronDown
        className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  );
}
SelectTrigger.displayName = 'SelectTrigger';

export function SelectValue({
  placeholder,
  children,
  value,
  options,
  foundLabel,
}: SelectValueProps & {
  value?: string;
  options?: Array<{ value: string; label: string }>;
  foundLabel?: string;
}) {
  // Normalize value - handle both string and number/empty string
  const stringValue = value !== undefined && value !== null && value !== '' ? String(value) : '';
  const hasValue = stringValue !== '' && stringValue !== 'undefined' && stringValue !== 'null';

  // Use useMemo to find label efficiently - will re-compute when foundLabel, stringValue, or options change
  // MUST be called before any early returns
  const selectedLabel = useMemo(() => {
    // Priority: foundLabel (from parent) > options > undefined
    if (foundLabel) {
      return foundLabel;
    }

    if (hasValue && options && options.length > 0) {
      const found = options.find(opt => {
        const optValue = String(opt.value).trim().toLowerCase();
        const val = String(stringValue).trim().toLowerCase();
        return optValue === val;
      });
      if (found) {
        return found.label;
      }
    }

    return undefined;
  }, [foundLabel, hasValue, stringValue, options]);

  // Early return after hooks
  if (children) {
    return <>{children}</>;
  }

  // Display: selected label if found and has value, otherwise placeholder
  // Always show label if we have a value and found a label, even if it's the same as placeholder
  const displayText = hasValue && selectedLabel ? selectedLabel : placeholder || '';
  const showPlaceholder = !hasValue || !selectedLabel;

  return <span className={showPlaceholder ? 'text-gray-500' : 'text-gray-900'}>{displayText}</span>;
}
SelectValue.displayName = 'SelectValue';

export function SelectContent({
  children,
  className = '',
  closing,
  onValueChange,
  currentValue,
  containerRef,
}: SelectContentProps & {
  closing?: boolean;
  onValueChange?: (value: string) => void;
  currentValue?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
}) {
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef?.current && !closing) {
      const updatePosition = () => {
        if (containerRef?.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          const dropdownHeight = 200; // max height

          let top = rect.bottom + 4;
          // If not enough space below, show above
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            top = rect.top - dropdownHeight - 4;
          }

          setPosition({
            top: Math.max(4, Math.min(top, window.innerHeight - dropdownHeight - 4)),
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      // Update on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [containerRef, closing]);

  const content = (
    <div
      ref={contentRef}
      data-select-content
      style={
        position
          ? {
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              zIndex: 10000,
              maxHeight: '200px',
            }
          : { display: 'none' }
      }
      className={`min-w-[8rem] overflow-y-auto rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg origin-top ${
        closing ? 'animate-dropdown-close' : 'animate-dropdown'
      } ${className}`}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      <div className="p-1">
        {React.Children.map(children, child => {
          if (!React.isValidElement(child)) return null;
          const childType = child.type as any;
          if (childType?.displayName === 'SelectItem') {
            return React.cloneElement(child as ReactElement<any>, {
              onValueChange,
              isSelected: currentValue === (child.props as any).value,
            });
          }
          return child;
        })}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
SelectContent.displayName = 'SelectContent';

export function SelectItem({
  children,
  value,
  className = '',
  disabled = false,
  onValueChange,
  isSelected,
}: SelectItemProps & {
  onValueChange?: (value: string) => void;
  isSelected?: boolean;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && onValueChange) {
      onValueChange(value);
    }
  };

  return (
    <div
      onClick={handleClick}
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={`relative flex w-full select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none focus:bg-gray-100 focus:text-gray-900 hover:bg-gray-100 ${
        disabled ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${isSelected ? 'bg-gray-100' : ''} ${className}`}
    >
      {children}
    </div>
  );
}
SelectItem.displayName = 'SelectItem';
